'use client'

import { usePathname } from 'next/navigation'
import { ArrowRight } from 'lucide-react'
import { formatDateTimeNormal, formatPrice } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { bookloDisplayStatus } from '@/utils/order'

const STATUS_MAP: Record<string, { label: string, color: string }> = {
  pending: { label: 'Хүлээгдэж буй', color: '#fcd34d' },
  paid: { label: 'Төлөгдсөн', color: '#34d399' },
  ready: { label: 'Бэлэн болсон', color: '#93c5fd' },
  shipped: { label: 'Хүргэлтэнд', color: '#93c5fd' },
  delivered: { label: 'Хүргэгдсэн', color: '#34d399' },
  canceled: { label: 'Цуцлагдсан', color: '#fda4af' },
  refunded: { label: 'Бүтэн буцаасан', color: '#c4b5fd' },
  partiallyRefunded: { label: 'Хагас буцаасан', color: '#fda4af' },
  deliveryFailed: { label: 'Хүргэлт амжилтгүй', color: '#fda4af' },
}

export default function OrderCard({ order }: { order: any }) {
  const pathname = usePathname()
  const itemCount = order.products?.reduce((acc: number, p: any) => acc + (p.quantity || 1), 0) || 0
  const statusInfo = STATUS_MAP[bookloDisplayStatus(order, order.status)] || { label: 'Тодорхойгүй', color: 'rgba(255,255,255,0.55)' }

  return (
    <a
      href={navigateUrl(`/order/${order.customId}`, pathname)}
      className="grid grid-cols-12 gap-3 sm:gap-4 items-center py-5 cursor-pointer transition-colors hover:bg-white/2"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="col-span-1">
        <span className="inline-flex w-2 h-2 rounded-full" style={{ background: statusInfo.color }} />
      </div>
      <div className="col-span-7 sm:col-span-4 min-w-0">
        <div className="text-[11px] font-mono tabular-nums tracking-[0.14em] truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {formatDateTimeNormal(order.createdAt)}
        </div>
        <div className="mt-0.5 text-[14px] sm:text-[15px] font-semibold font-mono tabular-nums text-white truncate">
          #{order.customId}
        </div>
      </div>
      <div className="hidden sm:block col-span-3 text-[12.5px] uppercase tracking-[0.16em] truncate" style={{ color: statusInfo.color }}>
        {statusInfo.label}
        {/* Pay-after-delivery moves payment and fulfilment independently, so the
            status label alone doesn't say whether the money has arrived. */}
        {order.payAfterDelivery && !order.paidAt && (
          <span className="ml-1.5 normal-case tracking-normal" style={{ color: '#fcd34d' }}>· Төлөөгүй</span>
        )}
      </div>
      <div className="hidden md:block col-span-2 text-[12.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
        {itemCount > 0 ? `${itemCount} бараа` : ''}
      </div>
      <div className="col-span-4 sm:col-span-2 text-right inline-flex items-center justify-end gap-2 sm:gap-3">
        <div className="text-[14px] sm:text-[15px] font-semibold tabular-nums text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
          {formatPrice(order.amount)}
        </div>
        <ArrowRight className="h-3.5 w-3.5 shrink-0" style={{ color: 'rgba(255,255,255,0.4)' }} />
      </div>
    </a>
  )
}
